import React, { useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ComponentSlot, type ServerUiComponent, ServerUiDomainProvider } from '@features/server-ui';
import { Box } from '@mui/material';
import { SentryErrorBoundary } from '@services/SentryService';
import { useEventTracking } from '@services/statsig';

import { semanticDomainComponents } from '../registry';

import { OptimizationModalProvider } from './OptimizationModalProvider';
import { TileSkeleton } from './TileSkeleton';

// One tile-sized skeleton for every lazy domain_payload tile in this scope, so
// each tile reserves its height before it resolves — no layout shift on load.
const SEMANTIC_TILE_SKELETONS = { byPrimitive: { domain_payload: TileSkeleton } };

/** Hover must last this long to count as attention (not a mouse fly-over). */
const HOVER_INTENT_MS = 500;

type Surface = 'home' | 'dashboard';

/**
 * A tile's drill-in link that also reports attention: a debounced, once-per-mount
 * `tile_hovered` (real hover intent, not noise) and a `tile_clicked` on drill-in.
 */
const TrackedTileLink: React.FC<{
  slug: string;
  surface?: Surface;
  organizationId?: string;
  children: React.ReactNode;
}> = ({ slug, surface, organizationId, children }) => {
  const { practiceMetrics } = useEventTracking();
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoveredOnce = useRef(false);

  useEffect(
    () => () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
    },
    [],
  );

  return (
    <Box
      component={RouterLink}
      to={`/metrics/${slug}`}
      onMouseEnter={() => {
        if (hoveredOnce.current) return;
        hoverTimer.current = setTimeout(() => {
          hoveredOnce.current = true;
          practiceMetrics.tileHovered({
            organization_id: organizationId,
            metric_slug: slug,
            surface,
          });
        }, HOVER_INTENT_MS);
      }}
      onMouseLeave={() => {
        if (hoverTimer.current) {
          clearTimeout(hoverTimer.current);
          hoverTimer.current = null;
        }
      }}
      onClick={() => {
        practiceMetrics.tileClicked({
          organization_id: organizationId,
          metric_slug: slug,
          surface,
        });
      }}
      sx={{
        display: 'block',
        color: 'inherit',
        textDecoration: 'none',
        borderRadius: 2.5,
        transition: 'transform 120ms ease, box-shadow 120ms ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
      }}
    >
      {children}
    </Box>
  );
};

/**
 * Renders semantic metric components through the real SDUI path:
 * ServerUiDomainProvider (semantic registry) → ComponentSlot → lazy resolve →
 * SemanticMetricTile. Each tile is isolated in an error boundary so one failure
 * never takes the surface down. `surface` distinguishes the home strip from the
 * full dashboard for analytics.
 */
export const SemanticTileGrid: React.FC<{
  components: ServerUiComponent[];
  entityId?: string;
  surface?: Surface;
}> = ({ components, entityId, surface }) => {
  const { practiceMetrics } = useEventTracking();
  // Impression: one `tile_shown` per rendered tile, once per mount (deduped by
  // slug). With the home strip's random rotation this is the per-slug
  // denominator for the funnel — how often each tile was actually surfaced.
  const reportedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    components.forEach((c, i) => {
      const slug = (c.placeholder as { slug?: string } | null)?.slug;
      if (!slug || reportedRef.current.has(slug)) return;
      reportedRef.current.add(slug);
      practiceMetrics.tileShown({
        organization_id: entityId,
        metric_slug: slug,
        position: i,
        surface,
      });
    });
  }, [components, entityId, surface, practiceMetrics]);

  return (
    <ServerUiDomainProvider
      components={semanticDomainComponents}
      skeletons={SEMANTIC_TILE_SKELETONS}
      containerKey="semantic_metrics"
      entityId={entityId}
    >
      <OptimizationModalProvider surface={surface} organizationId={entityId}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          {components.map((c, i) => {
            const slug = (c.placeholder as { slug?: string } | null)?.slug;
            const tile = (
              <SentryErrorBoundary>
                <ComponentSlot component={c} />
              </SentryErrorBoundary>
            );
            // Each tile clicks into its drill-in (loop chart + findings console).
            return slug ? (
              <TrackedTileLink key={slug} slug={slug} surface={surface} organizationId={entityId}>
                {tile}
              </TrackedTileLink>
            ) : (
              <Box key={`tile-${i}`}>{tile}</Box>
            );
          })}
        </Box>
      </OptimizationModalProvider>
    </ServerUiDomainProvider>
  );
};
